/* eslint-disable arrow-parens */
import { useEffect, useState, useCallback, useRef } from "react";
import { trackPromise } from "react-promise-tracker";
import { api } from "../utils/api";
import { prefetchService } from "../utils/commonFunctions/prefetchService";
import { enrollmentNotificationStore } from "../utils/socket/enrollmentNotification";
import { VEBEnrollmentSchema } from "../types/enrollment.types";

interface VEBEnrollmentData {
	VEBOpenEnrollment: any;
	VEBEnrollments: any[];
	dependents: any[];
	previousEnrollments: VEBEnrollmentSchema[];
}

export const useVEBEnrollmentData = () => {
	const [data, setData] = useState<VEBEnrollmentData | null>(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<Error | null>(null);
	const [modalPlans, setModalPlans] = useState<string[]>([]);
	const [isModalOpen, setIsModalOpen] = useState(false);

	const socketEventTimeRef = useRef<number | null>(null);
	const fetchCountRef = useRef(0);
	const isFetchingRef = useRef(false); // ✅ track if a fetch is already in progress

	const fetchFromAPI = useCallback(async () => {
		// ✅ Prevent parallel duplicate calls
		if (isFetchingRef.current) {
			console.log("[useVEBEnrollment] ⏳ Fetch already in progress, skipping duplicate");
			return;
		}

		isFetchingRef.current = true;
		fetchCountRef.current += 1;
		const callNumber = fetchCountRef.current;
		const fetchStart = performance.now();
		console.log(`[useVEBEnrollment] 🌐 API call #${callNumber} started...`);

		setLoading(true);
		setError(null);

		try {
			const result: VEBEnrollmentData = await trackPromise(api.pages.getVEBEnrollmentInitData());

			const apiElapsed = performance.now() - fetchStart;
			console.log(`[useVEBEnrollment] ✅ API call #${callNumber} done in ${apiElapsed.toFixed(0)}ms`);

			if (socketEventTimeRef.current) {
				const totalElapsed = performance.now() - socketEventTimeRef.current;
				console.log(`[useVEBEnrollment] ⚡ Total socket → data ready: ${totalElapsed.toFixed(0)}ms`);
				socketEventTimeRef.current = null;
			}

			setData(result);
		} catch (err) {
			console.error(`[useVEBEnrollment] ❌ API call #${callNumber} failed:`, err);
			setError(err as Error);
		} finally {
			setLoading(false);
			isFetchingRef.current = false; // ✅ release lock
		}
	}, []);

	// Keep ref in sync with latest fetchFromAPI
	const fetchFromAPIRef = useRef(fetchFromAPI);
	useEffect(() => {
		fetchFromAPIRef.current = fetchFromAPI;
	}, [fetchFromAPI]);

	// Fetch on mount
	useEffect(() => {
		console.log("[useVEBEnrollment] 🚀 Fetching fresh data on mount");
		fetchFromAPI();
	}, [fetchFromAPI]);

	// Subscribe to notification store — runs once only
	useEffect(() => {
		// Check for notifications that arrived before this page mounted
		const pending = enrollmentNotificationStore.getPending();
		if (pending) {
			console.log("[useVEBEnrollment] 📬 Found pending notification:", pending.planNames);
			enrollmentNotificationStore.clear();
			setModalPlans(pending.planNames);
			setIsModalOpen(true);
			socketEventTimeRef.current = performance.now();
			// ✅ Don't call fetch here — mount fetch above already handles it
			// Only re-fetch if mount fetch has already finished
			if (!isFetchingRef.current) {
				fetchFromAPIRef.current();
			}
		}

		const unsubscribe = enrollmentNotificationStore.subscribe((notification) => {
			console.log("[useVEBEnrollment] 🔔 Live notification received:", notification.planNames);
			socketEventTimeRef.current = performance.now();
			setModalPlans(notification.planNames);
			setIsModalOpen(true);
			enrollmentNotificationStore.clear();
			fetchFromAPIRef.current(); // ✅ safe — isFetchingRef guard prevents duplicates
		});

		return unsubscribe;
	}, []); // ✅ empty deps — subscribe once, never re-run

	// Manual refresh after form submit etc.
	const refresh = useCallback(() => {
		console.log("[useVEBEnrollment] 🔄 Manual refresh triggered");
		return fetchFromAPIRef.current();
	}, []);

	const closeModal = useCallback(() => {
		setIsModalOpen(false);
		setModalPlans([]);
	}, []);

	return {
		data,
		loading,
		error,
		refresh,
		isEnrollmentModalOpen: isModalOpen,
		enrollmentModalPlans: modalPlans,
		closeEnrollmentModal: closeModal
	};
};
